import React from 'react';
import { CircleAlert, LucideIcon, Lock, SearchX, WifiOff } from 'lucide-react';
import { Button } from '../ui';

interface ErrorFallbackProps {
  error?: Error;
  resetError?: () => void;
  title?: string;
  message?: string;
  showDetails?: boolean;
}

/**
 * Shared look for error states: icon, short title, one muted line, optional actions.
 * `tone="error"` tints the icon red for real failures; not-found/denied states stay grey.
 */
export const ErrorState: React.FC<{
  icon?: LucideIcon;
  tone?: 'neutral' | 'error';
  title: string;
  message?: string;
  details?: string;
  actions?: React.ReactNode;
  className?: string;
}> = ({ icon: Icon = CircleAlert, tone = 'neutral', title, message, details, actions, className = '' }) => (
  <div className={`flex flex-col items-center justify-center px-4 py-10 text-center font-ui ${className}`} role="alert">
    <span
      className={`inline-flex h-10 w-10 items-center justify-center rounded-full ${
        tone === 'error' ? 'bg-primary-50 text-primary-600' : 'bg-neutral-100 text-neutral-400'
      }`}
    >
      <Icon className="h-[18px] w-[18px]" strokeWidth={1.75} aria-hidden="true" />
    </span>
    <h3 className="mt-3 text-[15px] font-normal tracking-[-0.01em] text-ink">{title}</h3>
    {message && <p className="mt-1 max-w-xs text-[13px] text-neutral-500">{message}</p>}
    {details && (
      <details className="mt-3 w-full max-w-md text-left">
        <summary className="cursor-pointer text-center text-[12px] text-neutral-400 hover:text-ink">Details</summary>
        <pre className="mt-2 max-h-40 overflow-auto rounded-xl bg-neutral-50 p-3 font-ui-mono text-[11px] leading-relaxed text-neutral-600 ring-1 ring-inset ring-neutral-200">
          {details}
        </pre>
      </details>
    )}
    {actions && <div className="mt-4 flex items-center gap-2">{actions}</div>}
  </div>
);

export const ErrorFallback: React.FC<ErrorFallbackProps> = ({
  error,
  resetError,
  title = 'Something went wrong',
  message = 'An unexpected error occurred. Please try again.',
  showDetails = false,
}) => (
  <ErrorState
    tone="error"
    title={title}
    message={message}
    details={showDetails && error ? `${error.message}${error.stack ? `\n\n${error.stack}` : ''}` : undefined}
    actions={
      resetError && (
        <Button variant="dark" onClick={resetError}>
          Retry
        </Button>
      )
    }
  />
);

// Specific error fallbacks for different scenarios
export const NetworkErrorFallback: React.FC<{ onRetry?: () => void }> = ({ onRetry }) => (
  <ErrorState
    icon={WifiOff}
    tone="error"
    title="Connection error"
    message="Check your connection and try again."
    actions={
      onRetry && (
        <Button variant="dark" onClick={onRetry}>
          Retry
        </Button>
      )
    }
  />
);

export const NotFoundErrorFallback: React.FC = () => (
  <ErrorState icon={SearchX} title="Not Found" message="The requested resource could not be found." />
);

export const UnauthorizedErrorFallback: React.FC = () => (
  <ErrorState icon={Lock} title="Access Denied" message="You don't have permission to access this resource." />
);
